/** Local notifications + Web Push subscription. */

import { api } from './api'

export type NotifyPermission = 'granted' | 'denied' | 'default' | 'unsupported'

export function notificationSupport(): NotifyPermission {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission as NotifyPermission
}

export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission !== 'default') {
    return Notification.permission as NotifyPermission
  }
  return (await Notification.requestPermission()) as NotifyPermission
}

/**
 * Show a nudge. Prefers the service worker registration because notifications
 * shown that way survive the page and support actions; falls back to a plain
 * Notification when no SW is controlling the page (e.g. first load).
 */
export async function showNudge(
  title: string,
  body: string,
  tag: string,
): Promise<boolean> {
  if (notificationSupport() !== 'granted') return false

  const options: NotificationOptions = {
    body,
    tag,
    icon: '/app-icons/icon-192.png',
    badge: '/app-icons/icon-192.png',
    // Renotify needs a tag; together they replace the previous nudge for the
    // same store instead of stacking duplicates.
    requireInteraction: false,
  }

  try {
    const reg = await navigator.serviceWorker?.ready
    if (reg) {
      await reg.showNotification(title, options)
      return true
    }
  } catch {
    // Fall through to the direct constructor.
  }

  try {
    new Notification(title, options)
    return true
  } catch {
    return false
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

function encodeKey(buf: ArrayBuffer | null): string {
  if (!buf) return ''
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Subscribe this device to Web Push.
 *
 * This is the only notification path that reaches a *closed* app — on Android,
 * and on iOS 16.4+ once installed to the home screen. It's what makes the
 * household "Ping" feature real.
 *
 * Returns a human-readable reason when it can't subscribe, rather than failing
 * silently.
 */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'This browser does not support Web Push.' }
  }
  if ((await requestNotificationPermission()) !== 'granted') {
    return { ok: false, reason: 'Notification permission was not granted.' }
  }

  const { public_key, configured } = await api.vapid()
  if (!configured || !public_key) {
    return {
      ok: false,
      reason:
        'The server has no VAPID keys configured, so push cannot be delivered. ' +
        'Generate a pair with `docker compose run --rm api python -m app.cli vapid`.',
    }
  }

  const reg = await navigator.serviceWorker.ready
  const existing = await reg.pushManager.getSubscription()
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
    }))

  await api.subscribePush({
    endpoint: sub.endpoint,
    p256dh: encodeKey(sub.getKey('p256dh')),
    auth: encodeKey(sub.getKey('auth')),
  })
  return { ok: true }
}
